"use client";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Badge, Button, Card, Row, SectionHeader, TopBar } from "@/components/ui";
import { useOrder } from "@/hooks/useOrder";
import { localize } from "@/hooks/useMenu";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { paymentApi } from "@/api/services";
import { Price } from "@/components/Price";
import {
  clearPendingPaymentReturn,
  readPendingPaymentReturn,
} from "@/lib/pendingPayment";
import { useBranch } from "@/store/branch";
import { OrderStatusEnum, PaymentStatusEnum } from "@/api/online-ordering-service/model";
import { paymentSplitApi, type PaymentSplit, type CoverageReport } from "@/api/payment-split";
import { useGroupCart } from "@/store/group-cart";
import { AttributionChip } from "@/components/group/AttributionChip";
import type { GroupCartParticipant } from "@/api/group-cart";
import { useCart } from "@/store/cart";
import { getToken } from "@/api/http";
import { readParticipant } from "@/lib/participant-token";
import { payerForOrder } from "@/lib/payer-token";
import { PayerNameSheet } from "@/components/dinein/PayerNameSheet";
import { OrderAccessError } from "@/components/dinein/OrderAccessError";
import { ShareBillButton } from "@/components/dinein/ShareBillButton";
import { classifyOrderError } from "@/lib/order-error";
import { useTable } from "@/store/table";
import { SignInModal } from "@/components/SignInModal";

function statusTone(status: OrderStatusEnum | undefined | null): "success" | "warn" | "danger" | "neutral" {
  if (status === OrderStatusEnum.Completed || status === OrderStatusEnum.Delivered) return "success";
  if (status === OrderStatusEnum.Cancelled || status === OrderStatusEnum.Rejected) return "danger";
  if (status === OrderStatusEnum.Preparing || status === OrderStatusEnum.ReadyForPickup) return "warn";
  return "neutral";
}

function paymentTone(status: PaymentStatusEnum | undefined | null): "success" | "warn" | "danger" | "neutral" {
  if (status === PaymentStatusEnum.Paid) return "success";
  if (status === PaymentStatusEnum.Unpaid) return "danger";
  if (status === PaymentStatusEnum.PartiallyPaid) return "warn";
  return "neutral";
}

export function DineInOrderScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { orderId } = useParams<{ orderId: string }>();
  const { t, i18n } = useTranslation();
  const { branchSlug, branchConfig } = useBranch();
  const { tableInfo } = useTable();
  const { format } = useFormatPrice();
  const [confirmingReturn, setConfirmingReturn] = useState(false);
  const { cart: groupCart } = useGroupCart();
  const { setAppendOrderId } = useCart();
  const [splits, setSplits] = useState<PaymentSplit[]>([]);
  const [coverage, setCoverage] = useState<CoverageReport | null>(null);
  const [identityReady, setIdentityReady] = useState<boolean>(
    () => !!getToken() || !!readParticipant() || !!payerForOrder(orderId ?? null),
  );
  const [signInOpen, setSignInOpen] = useState(false);

  // The BFF's get_order accepts a customer JWT, a group-participant token or
  // a dine-in payer token. An anonymous diner has none of the three until the
  // PayerNameSheet mints one, so fetching before `identityReady` guarantees a
  // 401 — hold the request instead. (Mirrors SplitPayScreen.)
  const { order, loading, error, refresh } = useOrder(orderId, {
    enabled: identityReady,
  });

  /** A QR deep link gives us branch+table, which is what the payer sheet needs. */
  const hasTableContext = !!tableInfo?.branchId && !!tableInfo?.tableId;
  const errorKind = error ? classifyOrderError(error) : null;

  // Pull split state on mount + whenever the order is refreshed (the dine-in
  // bill might have N concurrent payers; we want to show their progress).
  useEffect(() => {
    if (!orderId || !identityReady) return;
    let cancelled = false;
    paymentSplitApi
      .list(orderId)
      .then((res) => {
        if (cancelled) return;
        setSplits(res.splits);
        setCoverage(res.coverage);
      })
      .catch(() => {
        /* benign: split listing isn't critical for the receipt view */
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, order?.updatedAt, identityReady]);

  const participantMap = useMemo(() => {
    const m = new Map<string, GroupCartParticipant>();
    for (const p of groupCart?.participants ?? []) m.set(p.guestId, p);
    return m;
  }, [groupCart]);

  useEffect(() => {
    if (!orderId || !searchParams.has("paymentReturn")) return;
    const pending = readPendingPaymentReturn(orderId, branchSlug);
    if (!pending) {
      refresh();
      return;
    }
    let cancelled = false;
    setConfirmingReturn(true);
    paymentApi
      .confirmPayment(orderId, {
        intentId: pending.intentId || pending.transactionRef,
        transactionRef: pending.transactionRef,
        providerKey: pending.providerKey,
        raw: {
          paymentReturnUrl: window.location.href,
          providerFamily: pending.providerFamily,
        },
      })
      .then(() => {
        if (cancelled) return;
        clearPendingPaymentReturn(orderId, branchSlug);
        const next = new URLSearchParams(searchParams);
        next.delete("paymentReturn");
        next.delete("provider");
        setSearchParams(next, { replace: true });
        refresh();
      })
      .catch((err) => {
        console.warn("[DineInOrder] payment return confirmation failed", err);
        refresh();
      })
      .finally(() => {
        if (!cancelled) setConfirmingReturn(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchSlug, orderId, refresh, searchParams, setSearchParams]);

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    [i18n.language],
  );

  const payerSheet = (
    <PayerNameSheet
      open={!identityReady && !!orderId}
      orderId={orderId ?? ""}
      onEstablished={() => {
        setIdentityReady(true);
        void refresh();
      }}
      onCancel={() => navigate("/")}
    />
  );
  const signInModal = (
    <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
  );

  // No identity yet: the payer sheet IS the screen. When there's no table
  // context to mint a payer session from, the sheet can only tell them to
  // scan the QR — so also offer sign-in as the other way in.
  if (!identityReady) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.dine_in_order.title")} onBack={() => navigate("/")} />
        {!hasTableContext && (
          <OrderAccessError
            kind="identity"
            onSignIn={() => setSignInOpen(true)}
            onBack={() => navigate("/")}
          />
        )}
        {payerSheet}
        {signInModal}
      </div>
    );
  }

  if (loading && !order) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.dine_in_order.title")} onBack={() => navigate("/")} />
        <Card padding={20} style={{ margin: 16, textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>
          {t("v2.order_detail.loading")}
        </Card>
      </div>
    );
  }

  if (errorKind && !order) {
    // 401 with a table QR in hand → the payer sheet already covers it (the
    // stored payer token is bound to a different order); show the sheet
    // rather than an error card.
    const showSheetInstead = errorKind === "identity" && hasTableContext;
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.dine_in_order.title")} onBack={() => navigate("/")} />
        {!showSheetInstead && (
          <OrderAccessError
            kind={errorKind}
            onSignIn={() => setSignInOpen(true)}
            onRetry={() => void refresh()}
            onBack={() => navigate("/")}
          />
        )}
        {showSheetInstead && (
          <PayerNameSheet
            open
            orderId={orderId ?? ""}
            onEstablished={() => {
              setIdentityReady(true);
              void refresh();
            }}
            onCancel={() => navigate("/")}
          />
        )}
        {signInModal}
      </div>
    );
  }

  if (!order) return null;

  const subtotal = order.pricingSummary?.itemsSubtotal ?? 0;
  const serviceFee = order.pricingSummary?.serviceFeeAmount ?? 0;
  const tax = order.pricingSummary?.taxAmount ?? 0;
  const discount =
    (order.pricingSummary?.discountAmount ?? 0) +
    (order.pricingSummary?.loyaltyDiscountAmount ?? 0) +
    (order.pricingSummary?.promotionAmount ?? 0);
  const total = order.pricingSummary?.totalAmount ?? order.totalPaid ?? 0;
  const outstanding = order.outstandingBalance ?? Math.max(0, total - (order.totalPaid ?? 0));
  const orderDate = order.orderTimestamp ?? order.createdAt;
  const statusLabel = (order.status ?? t("v2.history.status.pending")).replace(/_/g, " ");
  const paymentLabel = (order.paymentStatus ?? t("v2.order_detail.pay_status.pending")).replace(/_/g, " ");

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar title={t("v2.dine_in_order.title")} onBack={() => navigate("/")} />

      <div style={{ padding: "12px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        {confirmingReturn && (
          <Card padding={14} style={{ background: "#eff6ff", borderColor: "#bfdbfe" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Icons.card size={16} style={{ color: "#1d4ed8", marginTop: 1 }} />
              <div style={{ fontSize: 13, color: "#1e40af", fontWeight: 600, lineHeight: 1.45 }}>
                {t("v2.tracking.confirming_payment")}
              </div>
            </div>
          </Card>
        )}
        {order.status === OrderStatusEnum.AwaitingPosConfirmation && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              padding: 14,
              borderRadius: "var(--radius-card)",
              background: "var(--surface-2)",
              border: "1px solid var(--border)",
            }}
          >
            <span className="spinner" aria-hidden />
            <div>
              <strong>
                {t(
                  "v2.tracking.awaiting_confirmation",
                  "Sit tight — we've sent your order to the restaurant",
                )}
              </strong>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--ink-2)" }}>
                {t(
                  "v2.tracking.awaiting_confirmation_sub",
                  "You'll see your order number as soon as the staff confirms it.",
                )}
              </p>
            </div>
          </div>
        )}

        <Card padding={16} style={{ background: "var(--ink-1)", color: "#fff" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 800, opacity: 0.75, textTransform: "uppercase" }}>
                <Icons.store size={13} /> {t("v2.dine_in_order.table_service")}
              </div>
              <div className="display" style={{ marginTop: 10, fontSize: 30, fontWeight: 800, letterSpacing: -0.5 }}>
                {order.tableNumber
                  ? t("v2.order_detail.table_label", { n: order.tableNumber })
                  : t("v2.checkout.dine_in")}
              </div>
              <div style={{ marginTop: 4, fontSize: 13, opacity: 0.72 }}>
                {order.orderNumber
                  ? t("v2.history.order_number", { n: order.orderNumber })
                  : t("v2.tracking.number_pending", "Pending")}
                {orderDate ? ` · ${dateFmt.format(new Date(orderDate))}` : ""}
              </div>
            </div>
            <Badge tone={statusTone(order.status)} style={{ textTransform: "capitalize" }}>
              {statusLabel}
            </Badge>
          </div>
        </Card>

        <Card padding={14}>
          <SectionHeader title={t("v2.dine_in_order.live_status")} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ padding: 12, borderRadius: "var(--radius-card)", background: "var(--surface-2)" }}>
              <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 700 }}>{t("v2.order_detail.status")}</div>
              <div className="display" style={{ marginTop: 5, fontSize: 14, fontWeight: 800, color: "var(--ink-1)", textTransform: "capitalize" }}>
                {statusLabel}
              </div>
            </div>
            <div style={{ padding: 12, borderRadius: "var(--radius-card)", background: "var(--surface-2)" }}>
              <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 700 }}>{t("v2.order_detail.payment")}</div>
              <div style={{ marginTop: 5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span className="display" style={{ fontSize: 14, fontWeight: 800, color: "var(--ink-1)", textTransform: "capitalize" }}>
                  {paymentLabel}
                </span>
                <Badge tone={paymentTone(order.paymentStatus)}>{outstanding > 0 ? <Price amount={outstanding} /> : t("v2.order_detail.paid")}</Badge>
              </div>
            </div>
          </div>
        </Card>

        <Card padding={14}>
          <SectionHeader title={t("v2.order_detail.items")} />
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {(order.orderLines ?? []).map((line, i) => {
              const name = localize(line.itemName, i18n.language) || t("v2.order_detail.item_default");
              const variants = (line.selectedVariants ?? [])
                .map((v) => localize(v.variantOptionName, i18n.language))
                .filter(Boolean);
              const modifiers = (line.selectedModifiers ?? [])
                .map((m) => {
                  const label = localize(m.modifierOptionName, i18n.language);
                  return label && m.quantity && m.quantity > 1 ? `${label} x${m.quantity}` : label;
                })
                .filter(Boolean);
              const price = line.pricingSummary?.totalAmount ?? line.pricingSummary?.subtotalAmount ?? 0;
              const lineRecord = line as unknown as {
                addedByGuestId?: string | null;
                addedByDisplayName?: string | null;
              };
              const adder = lineRecord.addedByGuestId
                ? participantMap.get(lineRecord.addedByGuestId)
                : null;
              return (
                <div
                  key={line.lineId ?? i}
                  data-testid="order-line"
                  data-line-id={line.lineId ?? ""}
                  data-name={localize(line.itemName, "en") || ""}
                  data-qty={line.quantity}
                  data-voided={(line as { voidedQuantity?: number }).voidedQuantity ?? 0}
                  data-total={price}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    paddingBottom: i < (order.orderLines ?? []).length - 1 ? 12 : 0,
                    borderBottom: i < (order.orderLines ?? []).length - 1 ? "1px solid var(--border-light, #f1f5f9)" : "none",
                  }}
                >
                  {/* Item Image */}
                  <div style={{ position: "relative", width: 56, height: 56, flexShrink: 0, borderRadius: 8, overflow: "hidden", backgroundColor: "var(--surface-2, #f8fafc)", border: "1px solid var(--border-light, #e2e8f0)" }}>
                    {line.imageUrl ? (
                      <img
                        src={line.imageUrl}
                        alt={name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                        🍔
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <div className="display" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1)" }}>
                        {line.quantity}x {name}
                      </div>
                      <div className="display" style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", color: "var(--ink-1)" }}>
                        <Price amount={price} />
                      </div>
                    </div>
                    {[...variants, ...modifiers].length > 0 && (
                      <div style={{ marginTop: 2, fontSize: 12, color: "var(--ink-2)", lineHeight: 1.45 }}>
                        {[...variants, ...modifiers].join(" · ")}
                      </div>
                    )}
                    {(adder || lineRecord.addedByDisplayName) && (
                      <div style={{ marginTop: 6 }}>
                        {adder ? (
                          <AttributionChip participant={adder} />
                        ) : (
                          <span
                            style={{
                              fontSize: 11,
                              color: "var(--ink-3)",
                              fontWeight: 600,
                            }}
                          >
                            {lineRecord.addedByDisplayName}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {(splits.length > 0 || (coverage && coverage.uncoveredLineIds.length > 0)) && (
          <Card padding={14}>
            <SectionHeader title={t("v2.order_detail.payments", "Payments")} />
            {splits.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--ink-3)" }}>
                {t("v2.split_pay.no_split_yet")}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {splits.map((s) => {
                  const payer = s.claimedByGuestId
                    ? participantMap.get(s.claimedByGuestId)
                    : null;
                  const stateKey = `v2.split_pay.split_state_${s.state}`;
                  return (
                    <div
                      key={s.splitId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 10px",
                        background: "var(--surface-2)",
                        borderRadius: 10,
                      }}
                    >
                      {payer && <AttributionChip participant={payer} />}
                      <span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
                        <Price amount={s.totalAmount} />
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: 999,
                          background:
                            s.state === "paid"
                              ? "var(--primary-50)"
                              : s.state === "failed"
                                ? "#fee2e2"
                                : s.state === "awaiting_collection"
                                  ? "#fef3c7"
                                  : "#dbe7fb",
                          color:
                            s.state === "paid"
                              ? "var(--primary-700)"
                              : s.state === "failed"
                                ? "#b91c1c"
                                : s.state === "awaiting_collection"
                                  ? "#92400e"
                                  : "#1f6feb",
                        }}
                      >
                        {t(stateKey, s.state)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {coverage && coverage.uncoveredLineIds.length > 0 && (
              <div
                style={{
                  marginTop: 10,
                  padding: "8px 10px",
                  background: "#fef3c7",
                  color: "#92400e",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {t("v2.split_pay.coverage_uncovered_count", {
                  count: coverage.uncoveredLineIds.length,
                  defaultValue: `${coverage.uncoveredLineIds.length} items still unclaimed`,
                })}
              </div>
            )}
          </Card>
        )}

        <Card padding={14}>
          <SectionHeader title={t("v2.order_detail.totals")} />
          <Row label={t("v2.order_detail.subtotal")} value={<Price amount={subtotal} />} />
          {serviceFee > 0 && (
            <Row label={t("v2.cart.service_tax")} value={<Price amount={serviceFee} />} />
          )}
          {tax > 0 && <Row label={t("v2.order_detail.tax")} value={<Price amount={tax} />} />}
          {discount > 0 && <Row label={t("v2.order_detail.discount")} value={<span>-<Price amount={discount} /></span>} />}
          <div style={{ borderTop: "1px solid var(--border)", marginTop: 6, paddingTop: 6 }}>
            <Row label={t("v2.order_detail.total")} value={<Price amount={total} />} bold />
            {outstanding > 0 && <Row label={t("v2.dine_in_order.outstanding")} value={<Price amount={outstanding} />} />}
            <span hidden data-testid="order-money" data-total={total} data-outstanding={outstanding} data-paid={order.totalPaid ?? 0} data-order-id={order.id ?? orderId} data-status={order.status} />
          </div>
        </Card>

        <div style={{ display: "flex", gap: 10, flexDirection: "column" }}>
          {outstanding > 0 ? (
            <>
              {String(branchConfig?.tenantId) === "136" && (
                <Button
                  variant="primary"
                  size="md"
                  full
                  testId="pay-full-bill"
                  onClick={() => navigate(`/orders/${order.id ?? orderId}/split?full=1`)}
                >
                  {t("v2.dine_in_order.pay_full_bill", "Pay full bill")}
                </Button>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <Button variant="outline" size="md" full testId="add-more" onClick={() => {
                  setAppendOrderId(order.id ?? orderId ?? null);
                  navigate("/");
                }}>
                  {t("v2.dine_in_order.add_more")}
                </Button>
                {String(branchConfig?.tenantId) === "136" && (
                  <Button
                    variant="outline"
                    size="md"
                    full
                    testId="pay-my-share"
                    onClick={() => navigate(`/orders/${order.id ?? orderId}/split`)}
                  >
                    {t("v2.split_pay.pay_my_share")}
                  </Button>
                )}
              </div>
            </>
          ) : (
            <div style={{ display: "flex", gap: 10 }}>
              <Button variant="outline" size="md" full testId="add-more" onClick={() => {
                setAppendOrderId(order.id ?? orderId ?? null);
                navigate("/");
              }}>
                {t("v2.dine_in_order.add_more")}
              </Button>
              <Button
                variant="primary"
                size="md"
                full
                onClick={() => navigate(`/orders/${order.id ?? orderId}`)}
              >
                {t("v2.dine_in_order.receipt")}
              </Button>
            </div>
          )}
        </div>

        {/* Invite anyone at the table (or not) to view and pay this bill. */}
        {String(branchConfig?.tenantId) === "136" && (
          <ShareBillButton
            orderId={order.id ?? orderId}
            displayNameHint={order.customerName ?? null}
            full
          />
        )}
      </div>

      {payerSheet}
      {signInModal}
    </div>
  );
}
